import { IDataListColumn, IJarvisUser, PaginatedAutoDataList, laravelPaginatedListFetchConfig } from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { createApplicationUserService } from "../shared/api/userService";
import { ApplicationUserAttachAction } from "./ApplicationUserAttachAction";
import { ApplicationUserBulkEditAction } from "./ApplicationUserBulkEditAction";
import { ApplicationUserListActions } from "./ApplicationUserListActions";
import { ApplicationUserRolesDrawer, useApplicationUserRoleDrawer } from "./ApplicationUserRolesDrawer";

export const ApplicationUserList: FC = () => {
	const { t } = useTranslation();
	const { applicationId = "" } = useParams<"applicationId">();
	const service = useMemo(() => createApplicationUserService(applicationId), [applicationId]);
	const { handleOpen, ...drawerProps } = useApplicationUserRoleDrawer();
	const dataListColumns: IDataListColumn<IJarvisUser>[] = useMemo(
		() => [
			{
				heading: t("First name"),
				valueKey: "firstName",
				type: "value",
			},
			{
				heading: t("Last name"),
				valueKey: "lastName",
				type: "value",
			},
			{
				heading: t("Email"),
				valueKey: "email",
				type: "value",
			},
			{
				heading: t("Roles"),
				valueKey: "roles",
				type: "value",
				render: (user) => user.roles.map((role) => role.name).join(", "),
			},
			{
				size: "max-content",
				type: "actions",
				heading: t("Actions"),
				action: (user) => <ApplicationUserListActions user={user} handleOpen={handleOpen} />,
			},
		],
		[handleOpen, t],
	);

	return (
		<>
			<PaginatedAutoDataList
				name="applicationUsers"
				columns={dataListColumns}
				empty={t("No attached users")}
				heading={t("Attached users")}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
					],
				}}
				headerActions={<ApplicationUserAttachAction />}
				listFetchConfig={laravelPaginatedListFetchConfig(service.listKey, service.index)}
				bulkEditActions={<ApplicationUserBulkEditAction applicationId={applicationId} />}
				enableBulkEdit={true}
				showSelectAllAction={false}
			/>
			{drawerProps.user !== null && (
				<ApplicationUserRolesDrawer applicationId={applicationId} {...drawerProps} user={drawerProps.user} />
			)}
		</>
	);
};
